import { Link } from '@tanstack/react-router';
import {
  FolderKanban,
  Workflow,
  CheckCircle,
  AlertCircle,
  Activity,
  TrendingUp,
} from 'lucide-react';
import { useDashboardStats } from '@/hooks';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
} from '@/components/ui';
import { formatRelativeTime, formatPercentage } from '@/lib/utils';
import type { ActivityItem } from '@/types';

export function Dashboard() {
  const { data: stats, isLoading, error } = useDashboardStats();

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-error mb-4" />
        <h2 className="text-lg font-medium text-foreground">Failed to load dashboard</h2>
        <p className="text-foreground-muted mt-1">Please try again later</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
        <p className="text-foreground-muted mt-1">
          Overview of your autonomous development platform
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Projects"
          value={stats?.projects.total}
          subtitle={`${stats?.projects.active ?? 0} active`}
          icon={<FolderKanban className="h-5 w-5" />}
          isLoading={isLoading}
        />
        <StatCard
          title="Total Workflows"
          value={stats?.workflows.total}
          subtitle={`${stats?.workflows.running ?? 0} running`}
          icon={<Workflow className="h-5 w-5" />}
          isLoading={isLoading}
        />
        <StatCard
          title="Success Rate"
          value={stats?.workflows.successRate !== undefined
            ? formatPercentage(stats.workflows.successRate)
            : undefined}
          icon={<TrendingUp className="h-5 w-5" />}
          trend={
            stats?.workflows.successRate !== undefined && stats.workflows.successRate >= 90
              ? 'positive'
              : 'neutral'
          }
          isLoading={isLoading}
        />
        <StatCard
          title="Running"
          value={stats?.workflows.running}
          icon={<Activity className="h-5 w-5 animate-pulse-subtle" />}
          isLoading={isLoading}
        />
      </div>

      {/* Recent activity */}
      <Card variant="bordered">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : stats?.recentActivity?.length ? (
            <div className="space-y-4">
              {stats.recentActivity.map((activity) => (
                <ActivityItemRow key={activity.id} activity={activity} />
              ))}
            </div>
          ) : (
            <p className="text-foreground-muted text-center py-8">
              No recent activity
            </p>
          )}
        </CardContent>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/projects">
          <Card variant="bordered" className="hover:border-accent transition-colors cursor-pointer">
            <CardContent className="flex items-center gap-4 py-6">
              <div className="p-3 rounded-lg bg-accent/10">
                <FolderKanban className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">View Projects</h3>
                <p className="text-sm text-foreground-muted">
                  Manage your development projects
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/workflows">
          <Card variant="bordered" className="hover:border-accent transition-colors cursor-pointer">
            <CardContent className="flex items-center gap-4 py-6">
              <div className="p-3 rounded-lg bg-accent/10">
                <Workflow className="h-6 w-6 text-accent" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">View Workflows</h3>
                <p className="text-sm text-foreground-muted">
                  Monitor workflow runs and status
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value?: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: 'positive' | 'negative' | 'neutral';
  isLoading?: boolean;
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  isLoading,
}: StatCardProps) {
  return (
    <Card variant="bordered">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-foreground-muted">{title}</p>
            {isLoading ? (
              <Skeleton className="h-8 w-20 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mt-1">{value ?? '-'}</p>
            )}
            {subtitle && (
              <p className="text-xs text-foreground-subtle mt-1">{subtitle}</p>
            )}
          </div>
          <div
            className={`p-2 rounded-lg ${
              trend === 'positive'
                ? 'bg-success/10 text-success'
                : trend === 'negative'
                ? 'bg-error/10 text-error'
                : 'bg-background-tertiary text-foreground-muted'
            }`}
          >
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ActivityItemRow({ activity }: { activity: ActivityItem }) {
  const iconMap = {
    workflow_completed: <CheckCircle className="h-4 w-4 text-success" />,
    workflow_failed: <AlertCircle className="h-4 w-4 text-error" />,
    project_created: <FolderKanban className="h-4 w-4 text-accent" />,
    settings_updated: <Activity className="h-4 w-4 text-foreground-muted" />,
  };

  const badgeVariantMap: Record<ActivityItem['type'], 'success' | 'error' | 'info' | 'default'> = {
    workflow_completed: 'success',
    workflow_failed: 'error',
    project_created: 'info',
    settings_updated: 'default',
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5">{iconMap[activity.type]}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">{activity.message}</p>
        <p className="text-xs text-foreground-subtle mt-0.5">
          {formatRelativeTime(activity.timestamp)}
        </p>
      </div>
      <Badge variant={badgeVariantMap[activity.type]}>
        {activity.type.replace('_', ' ')}
      </Badge>
    </div>
  );
}
